import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AvailabilityValue,
  BriefingChange,
  CommsTurn,
  ConfirmationStatus,
  Lang,
  MailKind,
  ReplyClass,
  ReplyVerdict,
  VolunteerImportPlan,
} from '@reditelji/shared';
import { mergeSaved } from './cache';
import type { ShiftAnswerView } from './types';
import { api, ApiRequestError } from './client';
import { useSession } from './session';
import type { MailBatch } from './mail';

export interface VolunteerRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  lang: Lang;
  gender: string;
  residence: string;
  age: number | null;
  experience: string;
  /** AVAILABILITY_SLOTS id → value; missing slots mean 'neznano'. */
  availability: Record<string, AvailabilityValue>;
  canMultiDay: boolean | null;
  otherTasks: boolean | null;
  /** Volunteer's comments/wishes from the signup sheet. */
  wishes: string;
  /** Internal coordinator notes. */
  note: string;
  scheduleVersion: number;
  active: boolean;
  /** Set when this row is a place in an external team, not a volunteer (SPEC §1.1J). */
  externalGroupId: string | null;
  externalSlotIndex: number | null;
  /** The real person, when the team has told us who is coming. */
  externalPersonName: string;
  /** Set when the person withdrew from the event — "Ne pride" (SPEC §1.1K). */
  cancelledAt: string | null;
  cancelledById: string | null;
  cancelledByName: string;
  cancelReason: string;
  hasToken?: boolean;
  tagIds: string[];
  confirmationStatus: ConfirmationStatus;
  /** The person's answer as a whole — its words and when it came; absent without comms:read (SPEC §1.1L). */
  confirmationComment?: string;
  confirmationAt?: string | null;
  /** Who gave the latest answer; a coordinator's id is shown by their name. */
  confirmationActorType?: 'coordinator' | 'volunteer' | 'system' | null;
  confirmationActorId?: string | null;
  /** Highest schedule version the volunteer ever confirmed (0 = never). */
  confirmedVersion: number;
  /** Whose turn their conversation is in (`commsTurn`); absent without comms:read. */
  commsTurn?: CommsTurn | null;
  /** Where each of their shifts stands, by shift id — the word alone, for every login (SPEC §1.1F). */
  shiftStatus: Record<string, ShiftAnswerView['status']>;
  /** The answer standing on each of their shifts, with its words and author; absent without comms:read. */
  shiftAnswers?: Record<string, ShiftAnswerView>;
  /** Newest publication of any kind — what the volunteer can see. */
  briefingSentAt?: string | null;
  briefingVersion?: number | null;
  /** Newest publication that was actually mailed; gates the change notice. */
  briefingMailedAt?: string | null;
  /** Notes between the organisation and the marshal team (SPEC §1.1L). */
  noteCount: number;
  /** The marshal team owes this thread an answer. */
  notesAwaitingTeam: boolean;
  /** For an organizer: a reply from the team they have not opened. */
  notesUnreadForMe: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MessageRow {
  id: string;
  volunteerId: string | null;
  direction: 'in' | 'out' | 'note';
  source: 'imap' | 'manual' | 'app';
  fromAddress: string;
  subject: string;
  body: string;
  at: string;
  /** The kind of mail it went out as — a template or an obvestilo; '' for free-form and inbound mail. */
  templateKey: MailKind | '';
  /** Ties the copies of one mail that landed in several threads (skupni predal). */
  copyGroupId: string | null;
  /** The coordinator who sent or logged it; null on arriving mail and on mail read from the predal. */
  createdById: string | null;
  /** Who else holds a copy — on a thread; the triage list carries none. */
  alsoTo?: { id: string; name: string }[];
  /** Whether it waits for an answer (`replyVerdicts`) — on a thread; null for an obvestilo. */
  reply?: ReplyVerdict | null;
  /** A coordinator's word on that, and whose — over the model and the rules. */
  replyOverride?: ReplyClass | null;
  replyOverrideBy?: string | null;
  createdAt: string;
}

function onError(err: Error) {
  notifications.show({
    color: 'red',
    title: 'Napaka',
    message:
      err instanceof ApiRequestError && err.status === 409
        ? 'Podatek je medtem spremenil nekdo drug. Osveži in poskusi znova.'
        : err.message,
  });
}

export function useVolunteers() {
  return useQuery({ queryKey: ['volunteers'], queryFn: () => api<VolunteerRow[]>('/volunteers') });
}

/** An answer given without writing: on the page, or recorded by a coordinator. */
export interface ThreadAnswer {
  at: string;
  status: 'potrjen' | 'zavrnjen';
  actorType: 'volunteer' | 'coordinator';
  actorId: string | null;
  comment: string;
}

/** A thread as the API reads it: its messages, the answers between them, and the word it hangs on. */
export interface ThreadPayload {
  messages: MessageRow[];
  answers: ThreadAnswer[];
  /** Why nobody waits on the deciding word when nobody does — `commsTurn`'s own reason. */
  deciding: { messageId: string; settled: 'answered' | 'not-chased' | null } | null;
}

function useThreadQuery<T>(volunteerId: string | null, select: (payload: ThreadPayload) => T) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['messages', volunteerId],
    queryFn: async (): Promise<ThreadPayload> => {
      try {
        return await api<ThreadPayload>(`/volunteers/${volunteerId!}/thread`);
      } catch (err) {
        // An API rolled back past the thread still lists the messages.
        if (!(err instanceof ApiRequestError) || err.status !== 404) throw err;
        const messages = await api<MessageRow[]>(`/volunteers/${volunteerId!}/messages`);
        return { messages, answers: [], deciding: null };
      }
    },
    select,
    enabled: volunteerId !== null && can('comms:read'),
  });
}

const messagesOf = (payload: ThreadPayload) => payload.messages;
const wholeThread = (payload: ThreadPayload) => payload;

/** The thread's messages — what the summary on Profil counts and cites. */
export function useThread(volunteerId: string | null) {
  return useThreadQuery(volunteerId, messagesOf);
}

/** The same request, whole: the messages with the answers and the deciding word. */
export function useThreadReading(volunteerId: string | null) {
  return useThreadQuery(volunteerId, wholeThread);
}

export function useTriage() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['messages', 'triage'],
    queryFn: () => api<MessageRow[]>('/messages/triage'),
    enabled: can('comms:write'),
  });
}

/** One reason "Povrni na objavljeno" is not offered, in the coordinator's words. */
export interface RevertBlocker {
  kind: string;
  detail: string;
  shiftId?: string;
}

/**
 * What the coordinator has changed since this volunteer's last publication —
 * the pin against the working state, which is the one pairing her own briefing
 * page never shows (SPEC §1.1F).
 */
export interface PendingChanges {
  pin: { version: number; sentAt: string; kind: 'mail' | 'silent' } | null;
  scheduleVersion: number;
  changes: BriefingChange[];
  /**
   * How many of them a revert would actually undo. Instruction and photo edits
   * belong to the post and everyone standing there reads them live, so a card
   * showing only those must not offer a button the server then refuses.
   */
  revertable: number;
  blockers: RevertBlocker[];
}

export function usePendingChanges(volunteerId: string | null) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['pending', volunteerId],
    queryFn: () => api<PendingChanges>(`/volunteers/${volunteerId!}/pending`),
    enabled: volunteerId !== null && can('comms:read'),
  });
}

/** A shift a "Ne pride" frees (SPEC §1.1K). */
export interface CancelFreedShift {
  shiftId: string;
  postId: string;
  postCode: string;
  postMicro: string;
  urgent: boolean;
  eventDayId: string;
  dayKey: string;
  dayLabel: string;
  date: string;
  fromMin: number;
  toMin: number;
  /** Started with nobody checked in: the post is empty right now. */
  running: boolean;
}

/** A shift that stays with the person, and why. */
export interface CancelKeptShift {
  shiftId: string;
  postCode: string;
  postMicro: string;
  dayLabel: string;
  date: string;
  fromMin: number;
  toMin: number;
  reason: { kind: 'past' } | { kind: 'checked-in'; inMin: number; outMin: number | null; running: boolean };
  label: string;
}

/** Once marked: who, when, why, and where each freed shift stands now. */
export interface CancelRecord {
  at: string;
  byId: string | null;
  byName: string;
  reason: string;
  /** By day and time; a slot deleted since is left out and counted in `freedTotal`. */
  freed: (CancelFreedShift & {
    now: { volunteerId: string | null; name: string | null; state: string; postDeleted: boolean };
  })[];
  /** How many the mark freed, deleted ones included. */
  freedTotal: number;
  kept: CancelKeptShift[];
  /** Still theirs but free to go now — a check-out since the mark ends what kept it. */
  stillFree: (CancelFreedShift & { updatedAt: string })[];
  /** Freed shifts that can still go back: open, at a live post, and not yet over. */
  returnable: string[];
  /** Whether handing all of those back keeps their answer as it was. */
  carries: boolean;
}

export interface CancelPlan {
  free: CancelFreedShift[];
  keep: CancelKeptShift[];
  minutes: number;
  /** Absent without comms:read (SPEC §1.1L), like the rest of the send status. */
  hasPin?: boolean;
  confirmation: { status: ConfirmationStatus; comment: string };
  /** Others on the same address, each marked on their own. Absent without comms:read. */
  siblings?: { id: string; name: string; freeCount: number }[];
  cancelled: CancelRecord | null;
}

/** The "Ne pride" dialog's plan, and once marked, the Ne pride card's record. */
export function useCancelPlan(volunteerId: string | null, enabled = true) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['cancel-plan', volunteerId],
    queryFn: () => api<CancelPlan>(`/volunteers/${volunteerId!}/cancel-plan`),
    enabled: volunteerId !== null && enabled && can('people:read'),
    // What the dialog lists is what the server has to still hold, so never
    // show it from a cache.
    staleTime: 0,
  });
}

export function useVolunteerMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['pending'] });
    void qc.invalidateQueries({ queryKey: ['shifts'] });
    void qc.invalidateQueries({ queryKey: ['timeline'] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
    void qc.invalidateQueries({ queryKey: ['mail', 'sends'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
    void qc.invalidateQueries({ queryKey: ['ai', 'summary'] });
    void qc.invalidateQueries({ queryKey: ['ai', 'summary-latest'] });
    void qc.invalidateQueries({ queryKey: ['cancel-plan'] });
  };
  const common = { onError, onSettled: invalidate };

  return {
    create: useMutation({
      mutationFn: (input: Partial<VolunteerRow>) =>
        api<VolunteerRow>('/volunteers', { method: 'POST', body: JSON.stringify(input) }),
      ...common,
    }),
    update: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; updatedAt: string } & Record<string, unknown>) =>
        api<VolunteerRow>(`/volunteers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      // A roster fetch already on its way read the row before this write, and
      // landing after it would put the old values back under the form.
      onMutate: () => qc.cancelQueries({ queryKey: ['volunteers'] }),
      onSuccess: (saved, { id, ...patch }) =>
        mergeSaved<VolunteerRow>(qc, ['volunteers'], id, saved, Object.keys(patch)),
      ...common,
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api<void>(`/volunteers/${id}`, { method: 'DELETE' }),
      ...common,
    }),
    setTags: useMutation({
      mutationFn: ({ id, tagIds }: { id: string; tagIds: string[] }) =>
        api(`/volunteers/${id}/tags`, { method: 'PUT', body: JSON.stringify({ tagIds }) }),
      ...common,
    }),
    issueToken: useMutation({
      mutationFn: (id: string) =>
        api<{ link: string }>(`/volunteers/${id}/token`, { method: 'POST' }),
      onError,
      onSettled: invalidate,
    }),
    /**
     * "Objavi po dogovoru" — first publication for somebody a mail cannot
     * reach (SPEC §1.1F). It never moves an existing pin; a silent edit does.
     */
    publish: useMutation({
      mutationFn: (id: string) =>
        api<{ ok: true }>(`/volunteers/${id}/publish`, { method: 'POST' }),
      ...common,
    }),
    /**
     * The other way out of an unpublished change: put the shifts back to the
     * schedule she is holding, rather than sending her the new one.
     */
    revert: useMutation({
      mutationFn: (id: string) =>
        api<{ restored: number; removed: number; confirmation: 'potrjen' | 'zavrnjen' | null }>(
          `/volunteers/${id}/revert`,
          { method: 'POST' },
        ),
      ...common,
    }),
    /**
     * "Ne pride" (SPEC §1.1K): free exactly what the dialog showed. A shift
     * that started or was checked in to since answers 409, and the dialog
     * reloads rather than freeing something nobody saw.
     */
    cancel: useMutation({
      mutationFn: (input: { id: string; reason: string; expectFree: string[]; expectRunning: string[] }) =>
        api<{ already: boolean; freed: CancelFreedShift[]; kept: CancelKeptShift[]; published: boolean }>(
          `/volunteers/${input.id}/cancel`,
          {
            method: 'POST',
            body: JSON.stringify({
              reason: input.reason,
              expectFree: input.expectFree,
              expectRunning: input.expectRunning,
            }),
          },
        ),
      onError: (err: Error) => {
        if (err instanceof ApiRequestError && err.status === 409) {
          notifications.show({
            color: 'yellow',
            title: 'Razpored se je medtem spremenil',
            message: 'Preveri, katere izmene se sprostijo, in potrdi znova.',
          });
          return;
        }
        onError(err);
      },
      onSettled: invalidate,
    }),
    /** "Vendarle pride…": hand back the freed shifts that are still open. */
    uncancel: useMutation({
      mutationFn: (input: { id: string; restore: string[] }) =>
        api<{
          restored: string[];
          notBack: { shiftId: string; reason: string }[];
          outcome: 'carried' | 'changed' | 'empty' | 'noop';
          version: number;
        }>(`/volunteers/${input.id}/uncancel`, {
          method: 'POST',
          body: JSON.stringify({ restore: input.restore }),
        }),
      ...common,
    }),
    sendBriefing: useMutation({
      mutationFn: (id: string) =>
        api<{ messageId: string; skipped?: string }>(`/volunteers/${id}/send-briefing`, {
          method: 'POST',
        }),
      ...common,
    }),
    // Starts a batch and returns once it exists — the mails follow in a job.
    sendBriefings: useMutation({
      mutationFn: (volunteerIds: string[]) =>
        api<MailBatch>('/volunteers/send-briefings', {
          method: 'POST',
          body: JSON.stringify({ volunteerIds }),
        }),
      ...common,
    }),
    setConfirmation: useMutation({
      mutationFn: (input: { volunteerId: string; status: ConfirmationStatus; comment?: string }) =>
        api(`/volunteers/${input.volunteerId}/confirmation`, {
          method: 'POST',
          body: JSON.stringify({ status: input.status, comment: input.comment ?? '' }),
        }),
      ...common,
    }),
    // Two steps, sharing one planner on the server: the preview writes
    // nothing, and the apply re-derives the same plan from the same CSV rather
    // than trusting one sent back from here.
    previewCsv: useMutation({
      mutationFn: (csv: string) =>
        api<VolunteerImportPlan>('/volunteers/import/preview', {
          method: 'POST',
          body: JSON.stringify({ csv }),
        }),
      onError,
    }),
    applyCsv: useMutation({
      mutationFn: (input: { csv: string; skipRows: number[] }) =>
        api<{ plan: VolunteerImportPlan; created: number; updated: number; skipped: number }>(
          '/volunteers/import/apply',
          { method: 'POST', body: JSON.stringify(input) },
        ),
      ...common,
    }),
    /** A coordinator's word on whether a message waits for an answer; `null` takes it back. */
    setReplyClass: useMutation({
      mutationFn: ({ messageId, cls }: { messageId: string; cls: ReplyClass | null }) =>
        api(`/messages/${messageId}/reply-class`, { method: 'PUT', body: JSON.stringify({ cls }) }),
      ...common,
    }),
    logMessage: useMutation({
      mutationFn: (input: {
        volunteerId: string;
        direction: 'in' | 'out' | 'note';
        subject?: string;
        body: string;
        /** Household members to file the same entry with. */
        alsoTo?: string[];
      }) =>
        api(`/volunteers/${input.volunteerId}/messages`, {
          method: 'POST',
          body: JSON.stringify({
            direction: input.direction,
            subject: input.subject ?? '',
            body: input.body,
            alsoTo: input.alsoTo ?? [],
          }),
        }),
      ...common,
    }),
    assignTriage: useMutation({
      mutationFn: (input: { messageId: string; volunteerId: string; alsoTo?: string[] }) =>
        api(`/messages/${input.messageId}/assign`, {
          method: 'POST',
          body: JSON.stringify({ volunteerId: input.volunteerId, alsoTo: input.alsoTo ?? [] }),
        }),
      ...common,
    }),
  };
}
